import { LEAD_SOURCE_KINDS, LEAD_SOURCE_KIND_LABELS, type CreateProductBody, type ProductAdminDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardHeader, IconButton, Input, Select, Switch, useToast } from '../../../components/ui';
import { useLookups } from '../../../hooks/useMeta';
import { api, errorMessage } from '../../../lib/api';
import { pluralize } from '../../../lib/format';
import { LookupEditor } from './LookupEditor';

export function CatalogSection() {
  return (
    <>
      <ProductsEditor />
      <LookupEditor
        config={{
          title: 'Product categories',
          description: 'Also used for “interested in” on each lead.',
          endpoint: '/api/settings/product-categories',
          nameField: 'label',
          activeToggle: true,
          usage: (item) => `${pluralize(item.productCount ?? 0, 'product')} · ${pluralize(item.interestCount ?? 0, 'interested lead')}`,
        }}
      />
      <LookupEditor
        config={{
          title: 'Lead sources',
          endpoint: '/api/settings/sources',
          nameField: 'name',
          activeToggle: true,
          kindOptions: LEAD_SOURCE_KINDS.map((kind) => ({ value: kind, label: LEAD_SOURCE_KIND_LABELS[kind] })),
          usage: (item) => pluralize(item.leadCount ?? 0, 'lead'),
        }}
      />
      <LookupEditor
        config={{
          title: 'Tags',
          description: 'Tags can also add or remove score points (Settings → Scoring).',
          endpoint: '/api/settings/tags',
          nameField: 'name',
          color: true,
          usage: (item) => `${pluralize(item.leadCount ?? 0, 'lead')}${item.scoringRuleCount ? ` · ${item.scoringRuleCount} scoring rule` : ''}`,
        }}
      />
    </>
  );
}

/** Ovens, mixers and parts you quote — used as deal line items. */
function ProductsEditor() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { meta } = useLookups();
  const categories = meta?.productCategories ?? [];
  const { data } = useQuery({ queryKey: ['settings', 'products'], queryFn: ({ signal }) => api.get<{ items: ProductAdminDTO[] }>('/api/settings/products', { signal }) });
  const [draft, setDraft] = useState({ name: '', sku: '', categoryKey: '', listPrice: '' });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['settings', 'products'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const update = useMutation({ mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/api/settings/products/${id}`, patch), onSuccess: refresh, onError });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/settings/products/${id}`), onSuccess: refresh, onError });
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/settings/products', {
        name: draft.name.trim(),
        sku: draft.sku.trim() || null,
        categoryKey: draft.categoryKey,
        listPrice: draft.listPrice === '' ? null : Number(draft.listPrice),
      } satisfies Partial<CreateProductBody>),
    onSuccess: async () => {
      setDraft({ name: '', sku: '', categoryKey: '', listPrice: '' });
      await refresh();
    },
    onError,
  });

  return (
    <Card>
      <CardHeader title="Products" description="What you sell. Products on past deals can be deactivated but not deleted." />
      <ul className="divide-y divide-slate-100">
        {(data?.items ?? []).map((product) => (
          <li key={product.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
            <Input size="sm" className="w-64" aria-label="Product name" defaultValue={product.name} onBlur={(e) => e.target.value.trim() && e.target.value !== product.name && update.mutate({ id: product.id, patch: { name: e.target.value.trim() } })} />
            <Input size="sm" className="w-28" aria-label="SKU" placeholder="SKU" defaultValue={product.sku ?? ''} onBlur={(e) => e.target.value !== (product.sku ?? '') && update.mutate({ id: product.id, patch: { sku: e.target.value.trim() || null } })} />
            <Select size="sm" aria-label="Category" value={product.categoryKey} onChange={(e) => update.mutate({ id: product.id, patch: { categoryKey: e.target.value } })}>
              {categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Input
              size="sm"
              className="w-28"
              type="number"
              min={0}
              step="0.01"
              aria-label="List price"
              placeholder="Price"
              defaultValue={product.listPrice ?? ''}
              onBlur={(e) => {
                const next = e.target.value === '' ? null : Number(e.target.value);
                if (next !== product.listPrice) update.mutate({ id: product.id, patch: { listPrice: next } });
              }}
            />
            <span className="text-xs text-slate-500">{product.currency}</span>
            <Switch size="sm" checked={product.isActive} onChange={(next) => update.mutate({ id: product.id, patch: { isActive: next } })} label="Active" />
            <span className="ml-auto text-xs text-slate-500">{pluralize(product.opportunityLineCount, 'deal line')}</span>
            <IconButton icon={Trash2} label={`Delete ${product.name}`} size="sm" variant="ghost" disabled={product.opportunityLineCount > 0} onClick={() => remove.mutate(product.id)} />
          </li>
        ))}
      </ul>
      <form
        className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Input size="sm" className="w-64" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="New product…" aria-label="New product name" />
        <Input size="sm" className="w-28" value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value })} placeholder="SKU" aria-label="New product SKU" />
        <Select size="sm" value={draft.categoryKey} onChange={(e) => setDraft({ ...draft, categoryKey: e.target.value })} placeholder="Category" aria-label="New product category">
          {categories.filter((c) => c.isActive).map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </Select>
        <Input size="sm" className="w-28" type="number" min={0} step="0.01" value={draft.listPrice} onChange={(e) => setDraft({ ...draft, listPrice: e.target.value })} placeholder="Price" aria-label="New product price" />
        <Button size="sm" type="submit" icon={Plus} loading={create.isPending} disabled={!draft.name.trim() || !draft.categoryKey}>
          Add product
        </Button>
      </form>
    </Card>
  );
}
